import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useJsApiLoader } from '@react-google-maps/api';

import {
  DEFAULT_OPTIONS,
  DEFAULT_ZOOM,
  SINGLE_STORE_ZOOM,
  US_CENTER,
  US_ZOOM,
  jsApiLoaderLibraries,
} from './useRetailStoresMap.constants';
import { createMarkerContent } from './useRetailStoresMap.utils';

import { useStoresContext } from '~/legacy/easy-estimator-page/context/storesContext';

export const useRetailStoresMap = () => {
  const {
    getters: {
      stores,
      selectedStore,
      googleMapsApiKey,
      zipCodeLocation,
      googleMapsMapId,
    },
    handlers: { handleStoreSelection },
  } = useStoresContext();
  const { loadError, isLoaded } = useJsApiLoader({
    googleMapsApiKey: googleMapsApiKey || '',
    libraries: jsApiLoaderLibraries,
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const mapRef = useRef<google.maps.Map | null>(null);

  const googleMapsOptions = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      mapId: googleMapsMapId,
    }),
    [googleMapsMapId]
  );

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      setMap(map);
      if (zipCodeLocation) {
        map.setCenter({
          lat: zipCodeLocation.latitude,
          lng: zipCodeLocation.longitude,
        });
        map.setZoom(DEFAULT_ZOOM);
      } else {
        map.setCenter(US_CENTER);
        map.setZoom(US_ZOOM);
      }
    },
    [zipCodeLocation]
  );

  useEffect(() => {
    if (!map || !isLoaded || !window.google) {
      return undefined;
    }

    markersRef.current.forEach((marker) => (marker.map = null));

    markersRef.current = stores.map((store) => {
      const isSelected = selectedStore?.companyId === store.companyId;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: store.latitude, lng: store.longitude },
        map: map,
        title: `${store.companyName} - ${store.address}`,
        content: createMarkerContent(isSelected),
      });

      marker.addListener('gmp-click', () => {
        handleStoreSelection(store)();
      });

      return marker;
    });

    const bounds = new google.maps.LatLngBounds();

    stores.forEach((store) =>
      bounds.extend({ lat: store.latitude, lng: store.longitude })
    );
    if (stores.length === 0 && zipCodeLocation) {
      map.setCenter({
        lat: zipCodeLocation.latitude,
        lng: zipCodeLocation.longitude,
      });
      map.setZoom(DEFAULT_ZOOM);
    } else if (stores.length === 0) {
      map.setCenter(US_CENTER);
      map.setZoom(US_ZOOM);
    } else if (stores.length === 1) {
      map.setCenter(markersRef.current[0].position as google.maps.LatLng);
      map.setZoom(SINGLE_STORE_ZOOM);
    } else {
      map.fitBounds(bounds);
    }

    return () => {
      markersRef.current.forEach((marker) => (marker.map = null));
      markersRef.current = [];
    };
  }, [
    handleStoreSelection,
    isLoaded,
    map,
    selectedStore?.companyId,
    stores,
    zipCodeLocation,
  ]);

  return {
    googleMapsError: loadError,
    isGoogleMapsLoaded: isLoaded,
    setMap,
    onMapLoad,
    googleMapsOptions,
  };
};
